import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

// Configure axios defaults
const api = axios.create({
  baseURL: '/api',        // use Nginx proxy at same origin
  withCredentials: true,  // send/receive session cookies
  headers: { 'Content-Type': 'application/json' },
});

function App() {
  // ALL STATE DECLARATIONS - INSIDE THE COMPONENT
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldMapping, setFieldMapping] = useState({});
  const [showMapping, setShowMapping] = useState(false);
  const [matchingResults, setMatchingResults] = useState({ matched: 0, total: 0 });
  const [savedMappings, setSavedMappings] = useState([]);
  const [notesConfig, setNotesConfig] = useState({});
  const [showNotesSection, setShowNotesSection] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [allowZeroBalance, setAllowZeroBalance] = useState(false);
  // Shopify configuration state
  const [shopifyConfig, setShopifyConfig] = useState({
    configured: false,
    storeUrl: '',
    hasApiKey: false,
    hasAccessToken: false,
    hasWebhookSecret: false
  });

  const [showShopifyForm, setShowShopifyForm] = useState(false);
  const [shopifyFormData, setShopifyFormData] = useState({
    apiKey: '',
    accessToken: '',
    storeUrl: '',
    webhookSecret: ''
  });

  // Define Shopify fields directly in frontend - EXPANDED VERSION
  const shopifyFields = [
    // Required fields
    { key: 'giftCardNumber', label: 'Gift Card Number', required: true, description: 'Original gift card identifier' },
    { key: 'remainingBalance', label: 'Remaining Balance', required: true, description: 'Current balance amount' },
    
    // Optional basic fields
    { key: 'dateSold', label: 'Date Sold', required: false, description: 'When gift card was sold' },
    { key: 'lastUsed', label: 'Last Used', required: false, description: 'Last transaction date' },
    { key: 'soldBy', label: 'Sold By', required: false, description: 'Staff member who sold it' },
    { key: 'invoiceNumber', label: 'Invoice Number', required: false, description: 'Original invoice reference' },
    { key: 'originalAmount', label: 'Original Amount', required: false, description: 'Initial gift card value' },
    { key: 'amountSpent', label: 'Amount Spent', required: false, description: 'Previously used amount' },
    
    // Customer and expiration fields
    { key: 'customerEmail', label: 'Customer Email', required: false, description: 'Email of the customer who owns this gift card' },
    { key: 'customerName', label: 'Customer Name', required: false, description: 'Name of the customer who owns this gift card' },
    { key: 'customerPhone', label: 'Customer Phone', required: false, description: 'Phone number of the customer' },
    { key: 'expirationDate', label: 'Expiration Date', required: false, description: 'When the gift card expires (YYYY-MM-DD format)' },
    { key: 'issuedDate', label: 'Issued Date', required: false, description: 'When the gift card was originally issued' },
    { key: 'tags', label: 'Tags', required: false, description: 'Tags to categorize the gift card' },
    { key: 'location', label: 'Location/Store', required: false, description: 'Store location where it was sold' },
    { key: 'paymentMethod', label: 'Payment Method', required: false, description: 'How the gift card was paid for' }
  ];

  // AUTHENTICATION FUNCTIONS
  const checkAuthStatus = async () => {
    try {
      const response = await axios.get('/api/check-auth');
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/login', loginData);
      setIsAuthenticated(true);
      setUser(response.data.user);
      setLoginData({ username: '', password: '' });
    } catch (error) {
      alert('Login failed: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/logout');
      setIsAuthenticated(false);
      setUser(null);
      setCsvData({ headers: [], rows: [] });
      setFieldMapping({});
      setNotesConfig({});
      setShowMapping(false);
      setShowNotesSection(false);
      setMatchingResults({ matched: 0, total: 0 });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // SHOPIFY CONFIGURATION FUNCTIONS
const checkShopifyConfig = async () => {
  try {
    console.log('📋 Checking Shopify config with axios...');
    
    const response = await axios.get('/api/shopify-config');
    setShopifyConfig(response.data);
    
    console.log('✅ Shopify config retrieved:', response.data);
    
  } catch (error) {
    console.error('Failed to check Shopify config:', error);
    
    // Don't show alert for this one, just log it
    if (error.response?.status !== 401) {
      console.error('Shopify config check error:', error.response?.data || error.message);
    }
  }
};


const handleShopifyConfigSubmit = async (e) => {
  e.preventDefault();
  
  console.log('🚀 Starting Shopify config submission with axios');
  
  try {
    // First test the connection using axios (like your login)
    console.log('🧪 Testing server connection with axios...');
    const testResponse = await axios.get('/api/test');
    console.log('✅ Server connection test passed:', testResponse.data);
    
  } catch (testError) {
    console.error('❌ Server connection test failed:', testError);
    alert('Cannot connect to server. Please check if backend is running on port 5000.');
    return;
  }
  
  try {
    console.log('📡 Sending Shopify config request with axios...');
    
    const response = await axios.post('/api/shopify-config', {
      apiKey: shopifyFormData.apiKey,
      accessToken: shopifyFormData.accessToken,
      storeUrl: shopifyFormData.storeUrl,
      webhookSecret: shopifyFormData.webhookSecret
    });

    console.log('✅ Success response:', response.data);
    
    alert('Shopify configuration saved successfully!');
    setShowShopifyForm(false);
    checkShopifyConfig();
    
    setShopifyFormData({
      apiKey: '',
      accessToken: '',
      storeUrl: '',
      webhookSecret: ''
    });

  } catch (error) {
    console.error('❌ Axios error:', error);
    
    if (error.response) {
      // Server responded with an error status
      console.error('Server error:', error.response.data);
      alert(`Error ${error.response.status}: ${error.response.data.error || 'Server error'}`);
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
      alert('No response from server. Please check if backend is running.');
    } else {
      // Something else happened
      console.error('Request setup error:', error.message);
      alert(`Request error: ${error.message}`);
    }
  }
};

const downloadSummary = async (operationId) => {
    try {
        // FIX: Use the absolute URL to the backend server
        const response = await fetch(`http://localhost:5000/api/gift-cards/download-summary/${operationId}`, {
            method: 'GET',
            credentials: 'include' // Important for session-based auth
        });
        
        if (!response.ok) {
            const text = await response.text();
            console.error('Download failed:', text);
            throw new Error(`Server returned ${response.status}: ${text}`);
        }
        
        // Check if response is actually CSV
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('text/csv')) {
            const text = await response.text();
            console.error('Expected CSV but got:', contentType, text);
            // This is the error you were seeing
            throw new Error('Server returned HTML instead of CSV file');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gift-card-summary-${operationId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error('Download error:', error);
        // Display a more user-friendly error
        alert(`Failed to download summary: ${error.message}. Please check the console for details.`);
    }
};


  // Auto-matching function (frontend only)
  const autoMatchFields = (csvHeaders, shopifyFields) => {
    const mapping = {};
    let matchedCount = 0;

    const matchingPatterns = {
      giftCardNumber: ['gift card number', 'giftcard number', 'card number', 'gift_card_number', 'giftcardnumber', 'card_number', 'number', 'gc_number', 'gc number', 'voucher number', 'voucher_number', 'code', 'gift code', 'serial'],
      remainingBalance: ['remaining balance', 'balance', 'current balance', 'remaining_balance', 'current_balance', 'amount', 'value', 'remaining', 'balance_remaining', 'current_amount', 'available_balance', 'available balance'],
      dateSold: ['date sold', 'sold date', 'purchase date', 'date_sold', 'sold_date', 'purchase_date', 'created date', 'created_date', 'issue date', 'issue_date', 'sale date', 'sale_date', 'activation date'],
      lastUsed: ['last used', 'last_used', 'last use', 'last_use', 'last transaction', 'last_transaction', 'last activity', 'last_activity', 'used date', 'used_date', 'last_redemption', 'last redemption'],
      soldBy: ['sold by', 'sold_by', 'salesperson', 'sales person', 'staff', 'employee', 'seller', 'cashier', 'user', 'created by', 'created_by'],
      invoiceNumber: ['invoice number', 'invoice_number', 'invoice', 'receipt number', 'receipt_number', 'transaction id', 'transaction_id', 'order number', 'order_number', 'reference', 'ref', 'ticket number'],
      originalAmount: ['original amount', 'original_amount', 'initial amount', 'initial_amount', 'face value', 'face_value', 'denomination', 'issued amount', 'issued_amount', 'starting amount', 'starting_amount', 'full amount'],
      amountSpent: ['amount spent', 'amount_spent', 'used amount', 'used_amount', 'redeemed amount', 'redeemed_amount', 'spent', 'consumed', 'utilized amount', 'utilized_amount', 'deducted amount'],
      customerEmail: ['customer email', 'customer_email', 'email', 'customer mail', 'buyer email', 'buyer_email', 'purchaser email', 'owner email'],
      customerName: ['customer name', 'customer_name', 'customer', 'buyer name', 'buyer_name', 'purchaser name', 'purchaser_name', 'owner name', 'owner_name', 'full name'],
      customerPhone: ['customer phone', 'customer_phone', 'phone', 'phone number', 'phone_number', 'buyer phone', 'customer tel', 'telephone', 'mobile'],
      expirationDate: ['expiration date', 'expiration_date', 'expires', 'expires on', 'expires_on', 'expiry date', 'expiry_date', 'valid until', 'valid_until', 'end date'],
      issuedDate: ['issued date', 'issued_date', 'issue date', 'issue_date', 'created date', 'created_date', 'activation date', 'activation_date', 'start date'],
      tags: ['tags', 'tag', 'category', 'categories', 'type', 'classification', 'label', 'labels', 'group', 'segment'],
      location: ['location', 'store', 'shop', 'branch', 'outlet', 'store location', 'store_location', 'branch name', 'branch_name', 'point of sale'],
      paymentMethod: ['payment method', 'payment_method', 'payment type', 'payment_type', 'paid with', 'paid_with', 'payment', 'method', 'pay method']
    };

    const normalize = (str) => {
      return str.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    };

    shopifyFields.forEach(shopifyField => {
      const patterns = matchingPatterns[shopifyField.key] || [];
      let bestMatch = '';
      let bestScore = 0;

      csvHeaders.forEach(csvHeader => {
        const normalizedCsvHeader = normalize(csvHeader);
        
        patterns.forEach(pattern => {
          const normalizedPattern = normalize(pattern);
          
          if (normalizedCsvHeader === normalizedPattern) {
            bestMatch = csvHeader;
            bestScore = 100;
            return;
          }
          
          if (normalizedCsvHeader.includes(normalizedPattern)) {
            const score = (normalizedPattern.length / normalizedCsvHeader.length) * 90;
            if (score > bestScore) {
              bestMatch = csvHeader;
              bestScore = score;
            }
          }
          
          if (normalizedPattern.includes(normalizedCsvHeader) && normalizedCsvHeader.length > 2) {
            const score = (normalizedCsvHeader.length / normalizedPattern.length) * 80;
            if (score > bestScore) {
              bestMatch = csvHeader;
              bestScore = score;
            }
          }
        });
      });

      if (bestScore > 60) {
        mapping[shopifyField.key] = bestMatch;
        matchedCount++;
      } else {
        mapping[shopifyField.key] = '';
      }
    });

    return { mapping, matchedCount };
  };

  // Parse CSV file in frontend
  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          reject(new Error('Empty CSV file'));
          return;
        }

        const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
        const rows = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          rows.push(row);
        }
        
        resolve({ headers, rows });
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };

  // Save mapping to localStorage
  const saveMappingToStorage = (mappingName, mapping, csvHeaders, notesConfig) => {
    try {
      const newMapping = {
        id: Date.now().toString(),
        name: mappingName,
        mapping: mapping,
        notesConfig: notesConfig,
        csvHeaders: csvHeaders,
        createdAt: new Date().toISOString(),
        lastUsed: new Date().toISOString()
      };

      const existingMappings = JSON.parse(localStorage.getItem('csvFieldMappings') || '[]');
      const updatedMappings = [...existingMappings, newMapping];
      
      localStorage.setItem('csvFieldMappings', JSON.stringify(updatedMappings));
      setSavedMappings(updatedMappings);
      
      return true;
    } catch (error) {
      console.error('Error saving mapping:', error);
      return false;
    }
  };

  // Load mapping from localStorage
  const loadMappingFromStorage = (mappingId) => {
    try {
      const savedMapping = savedMappings.find(m => m.id === mappingId);
      if (savedMapping) {
        setFieldMapping(savedMapping.mapping);
        setNotesConfig(savedMapping.notesConfig || {});
        
        const updatedMappings = savedMappings.map(m => 
          m.id === mappingId ? { ...m, lastUsed: new Date().toISOString() } : m
        );
        localStorage.setItem('csvFieldMappings', JSON.stringify(updatedMappings));
        setSavedMappings(updatedMappings);
        
        const matchedCount = Object.values(savedMapping.mapping).filter(value => value !== '').length;
        setMatchingResults({ matched: matchedCount, total: shopifyFields.length });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading mapping:', error);
      return false;
    }
  };

  // Delete mapping from localStorage
  const deleteMappingFromStorage = (mappingId) => {
    try {
      const updatedMappings = savedMappings.filter(m => m.id !== mappingId);
      localStorage.setItem('csvFieldMappings', JSON.stringify(updatedMappings));
      setSavedMappings(updatedMappings);
      return true;
    } catch (error) {
      console.error('Error deleting mapping:', error);
      return false;
    }
  };

  // Handle notes field selection
  const handleNotesFieldToggle = (fieldName) => {
    setNotesConfig(prev => {
      const newConfig = { ...prev };
      if (newConfig[fieldName] && newConfig[fieldName].selected) {
        newConfig[fieldName] = {
          ...newConfig[fieldName],
          selected: false
        };
      } else {
        newConfig[fieldName] = {
          selected: true,
          customName: fieldName,
          originalName: fieldName
        };
      }
      return newConfig;
    });
  };

  // Handle custom name change for notes fields
  const handleNotesCustomNameChange = (fieldName, customName) => {
    setNotesConfig(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        customName: customName
      }
    }));
  };

  // Generate notes preview
  const generateNotesPreview = () => {
    const selectedFields = Object.entries(notesConfig)
      .filter(([_, config]) => config && config.selected)
      .map(([fieldName, config]) => config.customName);
    
    if (selectedFields.length === 0) return 'No fields selected for notes';
    
    if (csvData.rows.length > 0) {
      const exampleValues = Object.entries(notesConfig)
        .filter(([_, config]) => config && config.selected)
        .map(([fieldName, config]) => {
          const value = csvData.rows[0][fieldName] || '';
          return `${config.customName}: ${value}`;
        });
      return exampleValues.join(' | ');
    }
    
    return selectedFields.join(' | ');
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a CSV file');
      return;
    }

    setLoading(true);

    try {
      const parsedData = await parseCSV(selectedFile);
      setCsvData(parsedData);
      setShowMapping(true);
      setShowNotesSection(true);
      
      const { mapping, matchedCount } = autoMatchFields(parsedData.headers, shopifyFields);
      setFieldMapping(mapping);
      setMatchingResults({ 
        matched: matchedCount, 
        total: shopifyFields.length 
      });
      
      const initialNotesConfig = {};
      parsedData.headers.forEach(header => {
        initialNotesConfig[header] = {
          selected: false,
          customName: header,
          originalName: header
        };
      });
      setNotesConfig(initialNotesConfig);
      
    } catch (error) {
      alert('Upload failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (shopifyField, csvField) => {
    setFieldMapping(prev => {
      const newMapping = { ...prev, [shopifyField]: csvField };
      
      const matchedCount = Object.values(newMapping).filter(value => value !== '').length;
      setMatchingResults({ matched: matchedCount, total: shopifyFields.length });
      
      return newMapping;
    });
  };

  const handleAutoMatch = () => {
    const { mapping, matchedCount } = autoMatchFields(csvData.headers, shopifyFields);
    setFieldMapping(mapping);
    setMatchingResults({ 
      matched: matchedCount, 
      total: shopifyFields.length 
    });
  };

  const handleClearMapping = () => {
    const emptyMapping = {};
    shopifyFields.forEach(field => {
      emptyMapping[field.key] = '';
    });
    setFieldMapping(emptyMapping);
    setMatchingResults({ matched: 0, total: shopifyFields.length });
  };

  const handleSaveMapping = () => {
    const requiredFields = shopifyFields.filter(field => field.required);
    const unmappedRequired = requiredFields.filter(field => !fieldMapping[field.key]);
    
    if (unmappedRequired.length > 0) {
      alert(`Please map the following required fields: ${unmappedRequired.map(f => f.label).join(', ')}`);
      return;
    }

    const mappingName = prompt('Enter a name for this mapping:');
    if (mappingName && mappingName.trim()) {
      const success = saveMappingToStorage(mappingName.trim(), fieldMapping, csvData.headers, notesConfig);
      if (success) {
        alert('Field mapping and notes configuration saved successfully!');
      } else {
        alert('Failed to save mapping. Please try again.');
      }
    }
  };

  const handleLoadMapping = (mappingId) => {
    const success = loadMappingFromStorage(mappingId);
    if (success) {
      alert('Mapping loaded successfully!');
    } else {
      alert('Failed to load mapping. Please try again.');
    }
  };

  const handleDeleteMapping = (mappingId) => {
    if (window.confirm('Are you sure you want to delete this mapping?')) {
      const success = deleteMappingFromStorage(mappingId);
      if (success) {
        alert('Mapping deleted successfully!');
      } else {
        alert('Failed to delete mapping. Please try again.');
      }
    }
  };

  const handleImportGiftCards = async () => {
  const requiredFields = shopifyFields.filter(field => field.required);
  const unmappedRequired = requiredFields.filter(field => !fieldMapping[field.key]);
  
  if (unmappedRequired.length > 0) {
    alert(`Please map the following required fields: ${unmappedRequired.map(f => f.label).join(', ')}`);
    return;
  }

  if (window.confirm(`Import ${csvData.rows.length} gift cards to Shopify?`)) {
    try {
      setIsImporting(true);
      setImportResults(null);
      
      const response = await axios.post('/api/gift-cards/import', {
        csvData,
        fieldMapping,
        notesConfig,
        allowZeroBalance
      });
      
      if (response.data.success) {
        // FIX: Set the results from the API response to the state
        setImportResults(response.data.results);
        
        alert(`Import completed! Operation ID: ${response.data.operationId}`);
        // The trackImportProgress function seems to be for a different implementation (Server-Sent Events)
        // and is not necessary for the current download-on-click functionality.
        // trackImportProgress(response.data.operationId);
      }
    } catch (error) {
      alert('Import failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsImporting(false);
    }
  }
};


  const trackImportProgress = (operationId) => {
    const eventSource = new EventSource(`http://localhost:5000/api/gift-cards/download-summary/${operationId}`);
    
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Progress update:', data);
    };
    
    eventSource.onerror = () => {
      eventSource.close();
    };
  };

  const handleValidateGiftCards = async () => {
    try {
      const response = await axios.post('/api/gift-cards/validate', {
        csvData,
        fieldMapping,
        allowZeroBalance
      });
      
      const { valid, invalid, totalValue } = response.data;
      alert(`Validation complete:\n✅ Valid: ${valid}\n❌ Invalid: ${invalid}\n💰 Total Value: $${totalValue.toFixed(2)}`);
    } catch (error) {
      alert('Validation failed: ' + (error.response?.data?.error || error.message));
    }
  };

  // Initialize on component mount
  useEffect(() => {
    const loadSavedMappings = () => {
      try {
        const saved = localStorage.getItem('csvFieldMappings');
        if (saved) {
          setSavedMappings(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Error loading saved mappings:', error);
      }
    };
    
    loadSavedMappings();
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (user) {
      checkShopifyConfig();
    }
  }, [user]);

  if (!isAuthenticated) {
    return (
      <div className="App">
        <div className="login-container">
          <h2>Login</h2>
          <form onSubmit={handleLogin}>
            <div>
              <input
                type="text"
                placeholder="Username (demo)"
                value={loginData.username}
                onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                required
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Password (demo123)"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                required
              />
            </div>
            <button type="submit">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Shopify Gift Cards CSV Mapper</h1>
        <div className="user-info">
          <span>Welcome, {user?.username}!</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <main className="main-content">
        {/* Shopify Configuration Section */}
        {user && (
          <div className="shopify-config-section">
            <div className="section-header">
              <h2>Shopify Configuration</h2>
              <div className="config-status">
                {shopifyConfig.configured ? (
                  <div className="config-status-good">
                    <span className="status-indicator success">✓</span>
                    <span>Connected to: {shopifyConfig.storeUrl}</span>
                    <button 
                      onClick={() => setShowShopifyForm(true)}
                      className="btn-secondary"
                    >
                      Update Credentials
                    </button>
                  </div>
                ) : (
                  <div className="config-status-missing">
                    <span className="status-indicator error">⚠</span>
                    <span>Shopify credentials not configured</span>
                    <button 
                      onClick={() => setShowShopifyForm(true)}
                      className="btn-primary"
                    >
                      Configure Shopify
                    </button>
                  </div>
                )}
              </div>
            </div>

            {showShopifyForm && (
              <div className="shopify-form-modal">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>Configure Shopify Credentials</h3>
                    <button 
                      onClick={() => setShowShopifyForm(false)}
                      className="close-btn"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={handleShopifyConfigSubmit} className="shopify-form">
                    <div className="form-group">
                      <label>Store URL *</label>
                      <input
                        type="text"
                        placeholder="your-store.myshopify.com"
                        value={shopifyFormData.storeUrl}
                        onChange={(e) => setShopifyFormData({
                          ...shopifyFormData,
                          storeUrl: e.target.value
                        })}
                        required
                      />
                      <small>Enter your Shopify store URL (without https://)</small>
                    </div>

                    <div className="form-group">
                      <label>API Key *</label>
                      <input
                        type="text"
                        value={shopifyFormData.apiKey}
                        onChange={(e) => setShopifyFormData({
                          ...shopifyFormData,
                          apiKey: e.target.value
                        })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Access Token *</label>
                      <input
                        type="password"
                        value={shopifyFormData.accessToken}
                        onChange={(e) => setShopifyFormData({
                          ...shopifyFormData,
                          accessToken: e.target.value
                        })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Webhook Secret</label>
                      <input
                        type="text"
                        value={shopifyFormData.webhookSecret}
                        onChange={(e) => setShopifyFormData({
                          ...shopifyFormData,
                          webhookSecret: e.target.value
                        })}
                      />
                      <small>Optional: Used for webhook validation</small>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        Save Configuration
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setShowShopifyForm(false)}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="upload-section">
          <h2>Upload CSV File</h2>
          <form onSubmit={handleFileUpload}>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Processing...' : 'Upload & Parse'}
            </button>
          </form>
        </div>

        {/* Saved Mappings Section */}
        {savedMappings.length > 0 && (
          <div className="saved-mappings-section">
            <h2>Saved Mappings</h2>
            <div className="mappings-list">
              {savedMappings.map((mapping) => (
                <div key={mapping.id} className="mapping-item">
                  <div className="mapping-info">
                    <h4>{mapping.name}</h4>
                    <p>Created: {new Date(mapping.createdAt).toLocaleDateString()}</p>
                    <p>Headers: {mapping.csvHeaders.join(', ')}</p>
                    {mapping.notesConfig && Object.keys(mapping.notesConfig).length > 0 && (
                      <p>Notes fields: {Object.entries(mapping.notesConfig)
                        .filter(([_, config]) => config && config.selected)
                        .map(([_, config]) => config.customName)
                        .join(', ')}</p>
                    )}
                  </div>
                  <div className="mapping-actions">
                    <button 
                      onClick={() => handleLoadMapping(mapping.id)}
                      className="load-mapping-btn"
                      disabled={!showMapping}
                    >
                      📥 Load
                    </button>
                    <button 
                      onClick={() => handleDeleteMapping(mapping.id)}
                      className="delete-mapping-btn"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {csvData.headers.length > 0 && (
          <div className="csv-results">
            <h2>CSV Data Preview</h2>
            
            <div className="headers-section">
              <h3>CSV Headers ({csvData.headers.length} fields)</h3>
              <div className="headers-list">
                {csvData.headers.map((header, index) => (
                  <div key={index} className="header-item">
                    {header}
                  </div>
                ))}
              </div>
            </div>

            <div className="data-section">
              <h3>First 5 Rows</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    {csvData.headers.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.rows.slice(0, 5).map((row, index) => (
                    <tr key={index}>
                      {csvData.headers.map((header, headerIndex) => (
                        <td key={headerIndex}>{row[header] || ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showMapping && (
          <div className="mapping-section">
            <div className="mapping-header">
              <h2>Field Mapping</h2>
              <div className="mapping-stats">
                <span>Matched: {matchingResults.matched}/{matchingResults.total}</span>
                <div className="mapping-actions">
                  <button onClick={handleAutoMatch} className="auto-match-btn">
                    🎯 Auto Match
                  </button>
                  <button onClick={handleClearMapping} className="clear-mapping-btn">
                    🗑️ Clear All
                  </button>
                </div>
              </div>
            </div>

            <div className="mapping-grid">
              {shopifyFields.map((shopifyField) => (
                <div key={shopifyField.key} className={`mapping-row ${fieldMapping[shopifyField.key] ? 'mapped' : 'unmapped'}`}>
                  <div className="shopify-field">
                    <label>
                      <strong>{shopifyField.label}</strong>
                      {shopifyField.required && <span className="required">*</span>}
                    </label>
                    <p className="field-description">{shopifyField.description}</p>
                  </div>
                  
                  <div className="mapping-arrow">→</div>
                  
                  <div className="csv-field">
                    <select
                      value={fieldMapping[shopifyField.key] || ''}
                      onChange={(e) => handleMappingChange(shopifyField.key, e.target.value)}
                      className={`${shopifyField.required ? 'required-field' : ''} ${fieldMapping[shopifyField.key] ? 'mapped-select' : ''}`}
                    >
                      <option value="">Select CSV field...</option>
                      {csvData.headers.map((header, index) => (
                        <option key={index} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                    {fieldMapping[shopifyField.key] && (
                      <span className="mapping-indicator">✓ Mapped</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Notes Section */}
        {showNotesSection && Object.keys(notesConfig).length > 0 && (
          <div className="notes-section">
            <div className="notes-header">
              <h2>Custom Notes Configuration</h2>
              <p>Select fields to include in notes. Fields will be concatenated with " | " separator.</p>
            </div>

            <div className="notes-preview">
              <h3>Notes Preview:</h3>
              <div className="preview-text">
                {generateNotesPreview()}
              </div>
            </div>

            <div className="notes-fields">
              <h3>Available Fields for Notes:</h3>
              <div className="notes-grid">
                {csvData.headers.map((fieldName) => {
                  const config = notesConfig[fieldName] || { selected: false, customName: fieldName, originalName: fieldName };
                  const isMapped = Object.values(fieldMapping).includes(fieldName);
                  
                  return (
                    <div key={fieldName} className={`notes-field-row ${isMapped ? 'mapped-field' : ''}`}>
                      <div className="notes-checkbox">
                        <input
                          type="checkbox"
                          id={`notes-${fieldName}`}
                          checked={config.selected || false}
                          onChange={() => handleNotesFieldToggle(fieldName)}
                        />
                        <label htmlFor={`notes-${fieldName}`}>
                          <strong>{fieldName}</strong>
                          {isMapped && <span className="mapped-indicator"> (Mapped)</span>}
                        </label>
                      </div>
                      
                      {config.selected && (
                        <div className="notes-custom-name">
                          <label>Display as:</label>
                          <input
                            type="text"
                            value={config.customName || fieldName}
                            onChange={(e) => handleNotesCustomNameChange(fieldName, e.target.value)}
                            placeholder="Custom field name"
                          />
                        </div>
                      )}
                      
                      {config.selected && csvData.rows.length > 0 && (
                        <div className="notes-sample-value">
                          <span>Sample: {csvData.rows[0][fieldName] || 'N/A'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
{importResults && (
  <div className="import-results">
    <h3>Import Results</h3>
    <p>Successfully imported: {importResults.successful?.length || 0} gift cards</p>
    <p>Failed: {importResults.failed?.length || 0} gift cards</p>
    <p>Total value: ${importResults.totalValue?.toFixed(2) || '0.00'}</p>
    
    {/* Add download button */}
    {importResults.operationId && (
      <button 
        onClick={() => downloadSummary(importResults.operationId)}
        className="download-summary-btn"
        style={{
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #17a2b8, #138496)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          marginTop: '15px'
        }}
      >
        📥 Download Summary CSV
      </button>
    )}
  </div>
)}

        {showMapping && (
          <div className="action-buttons">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
              type="checkbox"
              checked={allowZeroBalance}
              onChange={e => setAllowZeroBalance(e.target.checked)}
              />
              Allow zero-balance gift cards
            </label>
            <button onClick={handleValidateGiftCards} className="validate-btn">
              ✅ Validate Gift Cards
            </button>
            <button
              onClick={handleImportGiftCards}
              disabled={isImporting}
              className="import-btn"
            >
              {isImporting ? 'Importing…' : 'Import Gift Cards'}
            </button>
            <button onClick={handleSaveMapping} className="save-mapping-btn">
              💾 Save Field Mapping & Notes
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
